import {
  clearAllKitchenBindCookies,
  hydrateKitchenStorageFromCookies,
  syncKitchenBindCookie,
  syncKitchenPwaManifestCookie,
  syncKitchenSetupCookie,
} from '@/utils/kitchenBindCookie';
import { clearKitchenPushPromptSkipped } from '@/utils/kitchenPushPrompt';
import { clearKitchenSession } from '@/utils/kitchenSession';
import { isStandalonePwa } from '@/utils/pwaStandalone';

const BIND_KEY = 'ollaa_kitchen_bind_token';
const SETUP_KEY = 'ollaa_kitchen_setup_complete';
const KIOSK_DEVICE_KEY = 'ollaa_kitchen_kiosk_device';
/** Set when this device opened a Küchen bind link (QR) — enables kiosk even if iPad misses standalone heuristics. */
const TABLET_INTENT_KEY = 'ollaa_kitchen_tablet_intent';
/** Set when Küchen-Manifest was active (A2HS on /kueche); survives iOS partner start_url. */
const PWA_MANIFEST_KEY = 'ollaa_kitchen_pwa_manifest';
const LEGACY_COMPANY_KEY = 'ollaa_kitchen_company_id';

if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem(LEGACY_COMPANY_KEY);
    hydrateKitchenStorageFromCookies();
  } catch {
    // ignore
  }
}

export const getKitchenBindToken = () => {
  if (typeof window === 'undefined') return '';
  hydrateKitchenStorageFromCookies();
  return String(window.localStorage.getItem(BIND_KEY) || '').trim();
};

export const hasKitchenBindToken = () => Boolean(getKitchenBindToken());

export const setKitchenBindToken = (bindToken) => {
  if (typeof window === 'undefined') return;
  const value = String(bindToken || '').trim();
  const previous = getKitchenBindToken();
  if (!value) {
    window.localStorage.removeItem(BIND_KEY);
    syncKitchenBindCookie('');
    return;
  }
  window.localStorage.setItem(BIND_KEY, value);
  window.localStorage.setItem(TABLET_INTENT_KEY, '1');
  syncKitchenBindCookie(value);
  if (previous && previous !== value) {
    clearKitchenSession();
  }
};

export const hasKitchenTabletIntent = () => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(TABLET_INTENT_KEY) === '1';
};

export const hasKitchenPwaManifestFlag = () => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(PWA_MANIFEST_KEY) === '1';
};

export const markKitchenPwaManifest = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(PWA_MANIFEST_KEY, '1');
  syncKitchenPwaManifestCookie(true);
};

export const clearKitchenCompanyBinding = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(BIND_KEY);
  window.localStorage.removeItem(SETUP_KEY);
  window.localStorage.removeItem(KIOSK_DEVICE_KEY);
  window.localStorage.removeItem(TABLET_INTENT_KEY);
  window.localStorage.removeItem(PWA_MANIFEST_KEY);
  clearAllKitchenBindCookies();
  clearKitchenPushPromptSkipped();
  clearKitchenSession();
};

export const isKitchenSetupComplete = () => {
  if (typeof window === 'undefined') return false;
  hydrateKitchenStorageFromCookies();
  return window.localStorage.getItem(SETUP_KEY) === '1';
};

export const markKitchenSetupComplete = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SETUP_KEY, '1');
  syncKitchenSetupCookie(true);
  if (isStandalonePwa()) {
    window.localStorage.setItem(KIOSK_DEVICE_KEY, '1');
  }
};

/** @deprecated use isKitchenKioskActive(); legacy localStorage flag from older builds */
export const isKitchenKioskDevice = () => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(KIOSK_DEVICE_KEY) === '1';
};

/** @deprecated use getKitchenBindToken */
export const getKitchenCompanyId = () => null;

/** @deprecated */
export const setKitchenCompanyId = () => {};
